import { useState, useMemo, createContext, useContext } from 'react';
import Api from '../../api/api';
import { isDemo } from '../GlobalContex';
import WidgetSources from '../components/widget/WidgetSources';

const stubRemoteWidget = new Proxy({}, { get() { return () => { }; } });
const WidgetsContext = createContext();
const isEmbeded = () => Api.isEmbeded();

WidgetsContext.displayName = 'WidgetsContext';

export default function WidgetsCtxComponent(children) {
  const [widgets, dispatchWidgets] = useState([]);
  const [remoteWidget, setRemoteWidget] = useState(stubRemoteWidget);
  const [applications, dispatchApplications] = useState([]);
  const [contextWidget, setCtxWidget] = useState({});

  const setWidgets = newWidget => {
    dispatchWidgets(state => [...state, newWidget]);
  };

  const [activeWidget, dispatchActiveWidget] = useState(widgets.length ? widgets[0] : {});
  const [activeWidgetMeta, setActiveWidgetMeta] = useState({});
  const dataWidgetsList = Object.keys(WidgetSources);

  async function loadWidgets() {
    // All widgets are temporarily loaded. TODO: provide a lazy page loader.
    let actualWidgets = [],
      pageIndex = 1;
    async function run(pageIndex) {
      const widgets = await Api.loadWidgets({ page: pageIndex });
      actualWidgets = actualWidgets.concat(widgets.data);
      if (+widgets.meta.lastPage >= pageIndex) {
        await run(++pageIndex);
      }
    }
    await run(pageIndex);
    dispatchWidgets(actualWidgets);
    // If there is no activeWidget, set it from the actualWidgets list.
    actualWidgets.length && !activeWidget.id && setActiveWidget(actualWidgets[0]);
    return actualWidgets;
  }

  async function loadApplications() {
    const actualApplications = await Api.loadApplications();
    if (!applications.length) dispatchApplications(actualApplications);
    return actualApplications;
  }

  function getWidgetbyId(id) {
    return widgets.find(item => item.id === Number(id));
  }

  async function createWidget({ app }) {
    const newWidget = await Api.createWidget({ app: { name: app } });
    setWidgets(newWidget);
    return newWidget;
  }

  async function getUrlInfo(url) {
    return await Api.getUrlInfo(url);
  }

  function isUrlValid(url) {
    return !!url.match(
      /((?:(?:http?|ftp)[s]*:\/\/)?[a-z0-9-%\/\&=?\.]+\.[a-z]{2,4}\/?([^\s<>\#%"\,\{\}\\|\\\^\[\]`]+)?)/gi
    );
  }

  async function removeWidget(widgetId) {
    await Api.removeWidget(widgetId);
    const newWidgetList = widgets.filter(el => el.id !== widgetId);
    dispatchWidgets(newWidgetList);
    setActiveWidget(null, newWidgetList);
  }

  async function changeProjectTitle(widgetId, title) {
    await Api.changeProjectTitle(widgetId, title);
  }

  function setActiveWidget(widget, list = widgets) {
    // Check the incoming widget. Embed mode can only load contextWidget.app widgets
    if (Api.isEmbeded() && contextWidget.app) {
      widget =
        widget && widget.app.name === contextWidget.app.name
          ? widget
          : list.find(widget => widget.app.name === contextWidget.app.name) || {};
    } else {
      if (!widget) {
        widget = list.length ? list[0] : {};
      }
    }

    if (Api.isEmbeded()) {
      Api.postActiveWidget({
        id: widget?.id,
        code: widget?.code,
        account: widget?.settings?.account,
        name: widget?.app?.name
      });
    }

    return dispatchActiveWidget({
      ...widget,
      settings: { params: {}, ...widget.settings, context: 'editor' }
    });
  }

  function setActiveWidgetSettings(settings) {
    const wgt = {
      ...activeWidget,
      settings: { ...activeWidget.settings, ...settings }
    };

    if (Api.isEmbeded()) {
      Api.postActiveWidget({ id: wgt.id, code: wgt.code, account: wgt?.settings?.account });
    }
    dispatchActiveWidget(wgt);
    saveWidget(wgt);
  }

  function setActiveWidgetSettingsParams(params) {
    const wgt = { ...activeWidget };
    wgt.settings.params = Object.assign(wgt.settings.params, params);
    dispatchActiveWidget(wgt);
    saveWidget(wgt);
  }

  async function saveWidget(widget) {
    if (!widget || !widget.id) return false;
    const update = {
      title: widget.title,
      settings: { ...widget.settings }
    };
    if (!isDemo) {
      await Api.updateWidget(widget.id, update);
    }
    dispatchWidgets(
      widgets.map(item => {
        if (item.id === widget.id) {
          return { ...item, ...update };
        }
        return item;
      })
    );
    return true;
  }

  const contextValue = useMemo(
    () => ({
      widgets,
      applications,
      loadWidgets,
      loadApplications,
      dispatchApplications,
      setWidgets,
      dispatchWidgets,
      activeWidget,
      setActiveWidget,
      remoteWidget,
      setRemoteWidget,
      activeWidgetMeta,
      setActiveWidgetMeta,
      createWidget,
      saveWidget,
      removeWidget,
      setActiveWidgetSettings,
      setActiveWidgetSettingsParams,
      dispatchActiveWidget,
      contextWidget,
      setCtxWidget,
      dataWidgetsList
    }),
    [widgets, activeWidget, activeWidgetMeta, remoteWidget, applications]
  );

  return <WidgetsContext.Provider value={contextValue}>{children}</WidgetsContext.Provider>;
}

WidgetsCtxComponent.context = WidgetsContext;
